import { useDataTableContext } from '../../components/DataTable/DataTableContext'
import useApiService from '../../services/api'
import lodash from 'lodash'
import { useMemo, useState } from 'react'
import confirm from '../../components/dialogConfirm'
import { CompanyTypeSlug, TableTypeSlug } from '../../models/companies'
import { Combobox } from '../../components/UI/combobox/combobox'
import { CreateEditCampaignModal } from './CreateEditCampaignModal'
import { CreateCampaignModalV2 } from './CreateCampaignModalV2'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useDropdownSuppression } from '../../components/UI/Dropdown/Dropdown'

export interface AssignToCampaignModalProps {
  accessorKey: string
  tableQueryKey: string[]
  requestParams: Record<string, any>
  tableTypeSlug: TableTypeSlug
  companyTypeSlug?: CompanyTypeSlug
  data: any[]
  buttonLess?: boolean
}

export function AssignToCampaignModal(props: AssignToCampaignModalProps) {
  const api = useApiService()
  const featureFlags = useFeatureFlagService()
  const isMultiType = !!featureFlags.enableEmailSequencing
  const { data: campaigns, isLoading } = api.useGetCampaigns()

  const mutation = api.useAssignToCampaignMutation(
    props.tableQueryKey,
    props.accessorKey,
    props.tableTypeSlug
  )
  const {
    state: { rowSelection, isAllRowsSelected, totalSelectedRows },
  } = useDataTableContext()

  const [openNewCampaign, setOpenNewCampaign] = useState(false)
  // When this picker is rendered inside a bulk-actions dropdown sub-menu,
  // opening the create-campaign modal must hide that dropdown so it does
  // not linger on top of the modal (FIR-7473). We suppress (not close)
  // the menu so this component stays mounted and its post-create assign
  // logic survives. Resolves to a no-op outside a Dropdown.
  const dropdown = useDropdownSuppression()

  function handleOpenNewCampaign() {
    dropdown.suppress()
    setOpenNewCampaign(true)
  }

  function handleCloseNewCampaign() {
    setOpenNewCampaign(false)
    dropdown.restore()
  }

  const campaignOptions = useMemo(
    () =>
      campaigns?.results.map((c) => ({
        label: c.name,
        value: c.id,
        icon: (
          <div
            className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
            style={{ backgroundColor: c.color }}
          />
        ),
      })) ?? [],
    [campaigns]
  )

  const selectedRows = props.data?.filter((_, i) => {
    return Boolean(rowSelection?.[i])
  })

  const unselectedRows = props.data?.filter((_, i) => {
    return !rowSelection?.[i]
  })

  const selectedChainProxyIds = selectedRows?.map((r: any) =>
    lodash.get(r, props.accessorKey)
  ) as number[]

  const unselectedChainProxyIds = unselectedRows?.map((r: any) =>
    lodash.get(r, props.accessorKey)
  ) as number[]

  async function handleSubmit(
    campaignId: number | string,
    campaignName?: string,
    campaignColor?: string,
    campaignTypeSlug?: string
  ) {
    const selected = campaigns?.results.find((it) => it.id == campaignId)
    const targetTypeSlug = props.companyTypeSlug ?? ''
    // Use explicitly passed type (for newly created campaigns not yet in cache)
    // or fall back to cached campaign data
    const existingTypeSlug = campaignTypeSlug ?? selected?.type?.slug

    // Detect type mismatch: pushing type X targets to a type Y campaign
    const hasMismatch =
      !!existingTypeSlug &&
      !!targetTypeSlug &&
      existingTypeSlug !== targetTypeSlug

    // Hide the bulk-actions dropdown for the duration of the confirm
    // dialog / assignment so it does not linger on top of them (FIR-7473).
    // No-op outside a Dropdown.
    dropdown.suppress()
    try {
      if (hasMismatch) {
        const typeName = selected?.type?.name ?? existingTypeSlug
        const confirmed = await confirm(
          `Adding these targets to a ${typeName} campaign will clear the opportunity type, which limits the columns visible in your campaign targets table. Continue?`,
          'Opportunity Type Mismatch'
        )
        if (!confirmed) return
      } else {
        const confirmed = await confirm(
          `Confirm assigning ${totalSelectedRows} items to campaign?`,
          'Assign to Campaign'
        )
        if (!confirmed) return
      }

      await mutation.mutateAsync({
        campaign_name: campaignName ?? selected?.name ?? '',
        campaign_color: campaignColor ?? selected?.color ?? '',
        campaign_id: parseInt(campaignId.toString()),
        include_ids: isAllRowsSelected ? [] : selectedChainProxyIds,
        exclude_ids: unselectedChainProxyIds,
        filter_params: props.requestParams,
        target_type_slug: targetTypeSlug,
        clear_type: hasMismatch,
      })
    } finally {
      dropdown.restore()
    }
  }

  return (
    <>
      {!openNewCampaign && (
        <Combobox
          options={campaignOptions}
          searchPlaceholder="Search Campaigns..."
          placeholder="Push to Campaign"
          onSelectAltAction={handleSubmit}
          onAddNew={handleOpenNewCampaign}
          loading={isLoading || mutation.isPending}
          buttonLess={props.buttonLess}
        />
      )}
      {isMultiType || !props.companyTypeSlug ? (
        <CreateCampaignModalV2
          key={props.companyTypeSlug ?? 'none'}
          show={openNewCampaign}
          onHide={handleCloseNewCampaign}
          defaultTypeSlug={props.companyTypeSlug}
          onCreated={async (campaign) => {
            await handleSubmit(
              campaign.id,
              campaign.name,
              campaign.color,
              campaign.typeSlug
            )
          }}
        />
      ) : (
        <CreateEditCampaignModal
          isOpen={openNewCampaign}
          onClose={handleCloseNewCampaign}
          defaultCampaignCompanyType={props.companyTypeSlug}
          pushToCampaignOptions={{
            data: props.data,
            accessorKey: props.accessorKey,
            requestParams: props.requestParams,
            tableQueryKey: props.tableQueryKey,
            totalSelectedRows: totalSelectedRows,
          }}
        />
      )}
    </>
  )
}
